import { FormFieldType } from "@models/form";
import { DEFAULT_LANGUAGE, Language } from "@models/language";
import {
  FieldErrors,
  FieldValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormUnregister,
  UseFormWatch,
} from "react-hook-form";
import DynamicFormCheckbox from "./DynamicFormCheckbox";
import DynamicFormInput, { ArrayFieldProps } from "./DynamicFormInput";
import DynamicFormInputNumber from "./DynamicFormInputNumber";
import DynamicFormRadio from "./DynamicFormRadio";
import DynamicFormRichText from "./DynamicFormRichText";
import DynamicFormSelect from "./DynamicFormSelect";
import DynamicFormUpload from "./DynamicFormUpload";
import { Field, ImageUploadFormField } from "./types";

const DynamicFormField = ({
  field,
  errors,
  register,
  setValue,
  watch,
  isDirty: isDirty = true,
  language: language = DEFAULT_LANGUAGE,
  className: className = "",
  fieldArrayProps,
  unregister,
}: {
  field: Field;
  errors: FieldErrors<FieldValues>;
  register: UseFormRegister<FieldValues>;
  language?: Language;
  isDirty?: boolean;
  className?: string;
  setValue?: UseFormSetValue<FieldValues>;
  watch?: UseFormWatch<FieldValues>;
  fieldArrayProps?: ArrayFieldProps;
  unregister?: UseFormUnregister<FieldValues>;
}) => {
  if (
    !!fieldArrayProps &&
    ![FormFieldType.TEXT, FormFieldType.PASSWORD, FormFieldType.EMAIL].includes(
      field.type
    )
  ) {
    console.error(
      "DynamicFormField: only text fields are supported for array fields"
    );
    return <></>;
  }

  switch (field.type) {
    case FormFieldType.RICHTEXT:
      if (!setValue || !watch || !unregister) {
        console.error(
          "DynamicFormField: setValue, watch and unregister are required for rich text fields"
        );
        return <></>;
      }
      return (
        <DynamicFormRichText
          field={field}
          errors={errors}
          register={register}
          unregister={unregister}
          language={language}
          isDirty={isDirty}
          className={className}
          setValue={setValue}
          watch={watch}
        />
      );
    case FormFieldType.RADIO:
      return (
        <DynamicFormRadio
          field={field}
          errors={errors}
          register={register}
          language={language}
          isDirty={isDirty}
          className={className}
        />
      );
    case FormFieldType.CHECKBOX:
      return (
        <DynamicFormCheckbox
          field={field}
          errors={errors}
          register={register}
          language={language}
          isDirty={isDirty}
          className={className}
        />
      );
    case FormFieldType.SELECT:
      return (
        <DynamicFormSelect
          field={field}
          errors={errors}
          register={register}
          language={language}
          isDirty={isDirty}
          className={className}
        />
      );
    case FormFieldType.NUMBER:
      return (
        <DynamicFormInputNumber
          field={field}
          errors={errors}
          register={register}
          language={language}
          isDirty={isDirty}
          className={className}
          setValue={setValue}
        />
      );
    case FormFieldType.FILE:
      return (
        <DynamicFormUpload
          field={field as ImageUploadFormField}
          errors={errors}
          register={register}
          language={language}
          isDirty={isDirty}
          className={className}
        />
      );
    default: {
      return (
        <DynamicFormInput
          field={field}
          errors={errors}
          register={register}
          language={language}
          isDirty={isDirty}
          className={className}
          setValue={setValue}
          fieldArrayProps={fieldArrayProps}
        />
      );
    }
  }
};

export default DynamicFormField;
